import { twoFactorEnableSchema, type TwoFactorEnableInput } from '@cnss/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import { ShieldCheck } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { apiClient } from '@/lib/api-client';
import { messageErreurApi } from '@/lib/api-error';
import { useAuth } from '@/lib/auth-context';

interface SetupResponse {
  otpauthUrl: string;
  qrCodeDataUrl: string;
  manualEntryKey: string;
}

interface TwoFactorSetupPageProps {
  /**
   * Fourni uniquement lors d'un provisionnement forcé en cours de connexion
   * (rôle exigeant la 2FA, pas encore active — voir LoginPage) : dans ce cas
   * aucune session n'existe encore, et c'est ce jeton qui identifie
   * l'utilisateur auprès de l'API plutôt qu'un en-tête d'autorisation.
   * Omis lors d'une activation volontaire depuis un compte déjà connecté.
   */
  challengeToken?: string;
  /** Appelé une fois la 2FA activée. Par défaut, redirige vers le tableau de bord. */
  onSuccess?: () => void;
}

/**
 * Provisionne un secret TOTP côté serveur, affiche le QR code à scanner, puis
 * confirme l'activation avec le premier code généré par l'application
 * d'authentification. Utilisé dans deux contextes : provisionnement forcé
 * pendant la connexion (`challengeToken` fourni par `LoginPage`) ou activation
 * volontaire depuis un compte déjà authentifié.
 */
export function TwoFactorSetupPage({ challengeToken, onSuccess }: TwoFactorSetupPageProps) {
  const { refetchUser, completeTwoFactorSetup } = useAuth();
  const navigate = useNavigate();
  const [setup, setSetup] = useState<SetupResponse | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<TwoFactorEnableInput>({ resolver: zodResolver(twoFactorEnableSchema) });

  useEffect(() => {
    apiClient
      .post<SetupResponse>('/auth/2fa/setup', challengeToken ? { challengeToken } : undefined)
      .then(({ data }) => setSetup(data))
      .catch(() => setLoadError("Impossible de générer le secret 2FA. Réessayez plus tard."));
  }, [challengeToken]);

  const handleSuccess = () => {
    if (onSuccess) {
      onSuccess();
    } else {
      navigate('/', { replace: true });
    }
  };

  const onSubmit = async (values: TwoFactorEnableInput) => {
    setServerError(null);
    try {
      if (challengeToken) {
        // Provisionnement forcé : aucune session n'existe encore, l'activation
        // de la 2FA complète la connexion et établit la session (voir
        // apps/api/src/modules/auth/auth.routes.ts, /2fa/enable).
        await completeTwoFactorSetup(challengeToken, values.code);
      } else {
        await apiClient.post('/auth/2fa/enable', values);
        await refetchUser();
      }
      handleSuccess();
    } catch (error) {
      setServerError(messageErreurApi(error, 'Code incorrect ou expiré.'));
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/40 px-4 py-10">
      <Card className="w-full max-w-md">
        <CardHeader className="items-center text-center">
          <div className="mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-primary text-primary-foreground">
            <ShieldCheck className="h-6 w-6" aria-hidden />
          </div>
          <CardTitle className="text-xl">Activation de la double authentification</CardTitle>
          <CardDescription>
            Votre profil nécessite la double authentification (2FA). Scannez le QR code avec une
            application d&apos;authentification (Google Authenticator, Authy…), puis saisissez le
            code affiché.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {loadError && <p className="text-sm text-destructive">{loadError}</p>}

          {setup && (
            <>
              <div className="flex justify-center">
                <img
                  src={setup.qrCodeDataUrl}
                  alt="QR code de provisionnement de la double authentification"
                  className="h-48 w-48 rounded-md border border-border p-2"
                />
              </div>
              <div className="space-y-1 text-center">
                <p className="text-xs text-muted-foreground">
                  Impossible de scanner ? Saisissez cette clé manuellement :
                </p>
                <p className="break-all rounded-md bg-muted px-3 py-2 font-mono text-sm">
                  {setup.manualEntryKey}
                </p>
              </div>

              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
                <div className="space-y-2">
                  <Label htmlFor="code">Code de confirmation</Label>
                  <Input
                    id="code"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    className="text-center text-lg tracking-[0.5em]"
                    {...register('code')}
                  />
                  {errors.code && (
                    <p className="text-sm text-destructive">{errors.code.message}</p>
                  )}
                </div>
                {serverError && (
                  <p
                    role="alert"
                    className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive"
                  >
                    {serverError}
                  </p>
                )}
                <Button type="submit" className="w-full" disabled={isSubmitting}>
                  {isSubmitting ? 'Vérification…' : 'Activer la 2FA'}
                </Button>
              </form>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
